import { type FC } from 'react';
import cx from 'classnames';
import styles from './MenuOverlay.module.css';
import { UnstyledButton } from '../UnstyledButton';
import { ComingSoonBadge } from '../ComingSoonBadge';
import { PersonSimpleRunIcon } from '../SceneBannerCard/icons';
import { CheckIcon } from './icons';

export type InsideSceneItem = {
  id: string;
  name: string;
  /** Session-visited flag — drives the gold ✓ / empty-circle checkpoint. */
  visited: boolean;
};

type MenuSceneCardProps = {
  name: string;
  /** Card photo; `undefined` renders a neutral placeholder square. */
  photoSrc?: string;
  photoAlt: string;
  exploreLabel: string;
  /** Localized "{visited} of {total} spots", or `null` while the tour loads. */
  spotsText: string | null;
  insideScenes?: InsideSceneItem[];
  /**
   * The scene isn't built yet → the card is inert (not a button), text + photo
   * are dimmed, the Explore pill is dropped and a "Coming soon" badge sits over
   * the photo (design 2860:38263).
   */
  isLocked?: boolean;
  comingSoonLabel?: string;
  /** Enter the scene — the WHOLE card is clickable (design 2865:38574). */
  onEnter: () => void;
  /** Enter an inside-scene — the whole row is clickable. */
  onEnterInside?: (insideId: string) => void;
};

/**
 * One scene entry (used by both menu tabs).
 *
 * - No inside-scenes → a standalone white card.
 * - Has inside-scenes → the white card sits on a `.sceneGroup` gray panel
 *   (slightly lighter than the overlay) that extends below it to hold the
 *   inside-scene rows (white rows on the gray panel).
 *
 * The whole card navigates (the "Explore the scene" pill is decorative), and
 * each inside-scene row navigates too. Rows carry a checkpoint only — gold ✓
 * when the scene was visited this session, empty circle otherwise (no POI
 * counts; inside-scenes contribute a single pseudo-POI to the PARENT card's
 * counter instead — see `$hajjJourneyProgress`).
 */
export const MenuSceneCard: FC<MenuSceneCardProps> = ({
  name,
  photoSrc,
  photoAlt,
  exploreLabel,
  spotsText,
  insideScenes = [],
  isLocked = false,
  comingSoonLabel,
  onEnter,
  onEnterInside,
}) => {
  const content = (
    <>
      <span className={cx(styles.sceneText, isLocked && styles.dimmed)}>
        <span className={styles.sceneHeading}>
          {/* NO `dir="auto"` here (nor on the rows below): these are i18n
              strings, always in the document's own direction. `auto` resolves
              from the first strong character, which digit-only era titles
              ("605", "1890") and the "…" placeholder don't have — it fell back
              to LTR and left-aligned them inside the RTL card. */}
          <span className={styles.sceneName}>{name}</span>
          <span className={cx(styles.sceneSpots, !spotsText && styles.sceneSpotsMuted)}>
            {spotsText ?? '…'}
          </span>
        </span>
        {!isLocked && (
          <span className={styles.explorePill}>
            <PersonSimpleRunIcon />
            <span>{exploreLabel}</span>
          </span>
        )}
      </span>
      <span className={styles.scenePhoto}>
        {photoSrc ? (
          <img
            src={photoSrc}
            alt={photoAlt}
            loading="lazy"
            className={isLocked ? styles.dimmed : undefined}
          />
        ) : (
          <span
            className={cx(styles.photoPlaceholder, isLocked && styles.dimmed)}
            aria-hidden="true"
          />
        )}
        {isLocked && comingSoonLabel && (
          <ComingSoonBadge label={comingSoonLabel} className={styles.photoBadge} />
        )}
      </span>
    </>
  );

  // Locked → inert element, not a button: there is nothing to enter yet.
  const card = isLocked ? (
    <div className={styles.sceneCard}>{content}</div>
  ) : (
    <UnstyledButton className={styles.sceneCard} onClick={onEnter}>
      {content}
    </UnstyledButton>
  );

  if (insideScenes.length === 0) return card;

  return (
    <div className={styles.sceneGroup}>
      {card}
      <div className={styles.tree}>
        {insideScenes.map((inside) => (
          <UnstyledButton
            key={inside.id}
            className={styles.treeRow}
            onClick={() => onEnterInside?.(inside.id)}
          >
            <span className={styles.treeName}>{inside.name}</span>
            {inside.visited ? (
              <span className={cx(styles.treeBadge, styles.treeBadgeCheck)} aria-label="visited">
                <CheckIcon />
              </span>
            ) : (
              <span
                className={cx(styles.treeBadge, styles.treeBadgeEmpty)}
                aria-label="not visited"
              />
            )}
          </UnstyledButton>
        ))}
      </div>
    </div>
  );
};
